import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  Music2,
  Edit,
  Trash2,
  FileText,
  Send,
  ClipboardList,
  Check,
  Lock,
  ChevronRight,
  Phone,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { Header } from "@/components/Layout/Header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { EventStatusBadge } from "@/components/Events/EventStatusBadge";
import { EventForm } from "@/components/Events/EventForm";
import { QuoteForm } from "@/components/Quotes/QuoteForm";
import { TechnicalSheetForm } from "@/components/TechnicalSheets/TechnicalSheetForm";
import { PaymentTracking } from "@/components/Payments/PaymentTracking";
import { SendDocumentsDialog } from "@/components/Documents/SendDocumentsDialog";
import { useEvent, useDeleteEvent, useUpdateEvent } from "@/hooks/useEvents";
import { usePayments } from "@/hooks/usePayments";
import { useToast } from "@/hooks/useToast";
import {
  getDocumentsSentRequirements,
  getValidatedRequirements,
} from "@/utils/workflowValidation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: event, isLoading } = useEvent(id!);
  const { data: payments = [] } = usePayments(event?.quote?.id);
  const deleteEvent = useDeleteEvent();
  const updateEvent = useUpdateEvent();
  const { toast } = useToast();

  const [editOpen, setEditOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [technicalSheetOpen, setTechnicalSheetOpen] = useState(false);
  const [sendDocumentsOpen, setSendDocumentsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteEvent.mutateAsync(id!);
      toast({ title: "Événement supprimé" });
      navigate("/events");
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'événement",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex h-full items-center justify-center">
        <p>Événement non trouvé</p>
      </div>
    );
  }

  const docRequirements = getDocumentsSentRequirements(event);
  const validationRequirements = getValidatedRequirements(event, payments);

  const handleValidate = async () => {
    try {
      await updateEvent.mutateAsync({
        id: event.id,
        updates: { status: "Validated" },
      });
      toast({
        title: "Événement validé",
        description: "L'événement est maintenant confirmé et verrouillé",
      });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de valider l'événement",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex h-full flex-col">
      <Header title={event.name} />

      <div className="flex-1 space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditOpen(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Modifier
            </Button>
            <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="mr-2 h-4 w-4" />
              Supprimer
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl">{event.name}</CardTitle>
                    <CardDescription>
                      {event.event_type.join(", ")} - {event.event_format}
                    </CardDescription>
                  </div>
                  <EventStatusBadge status={event.status} />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">
                        {format(new Date(event.event_date), "d MMMM yyyy", {
                          locale: fr,
                        })}
                      </p>
                      {event.event_time && (
                        <p className="text-sm text-muted-foreground">
                          {event.event_time}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <p className="font-medium">{event.location}</p>
                  </div>

                  {event.organizer && (
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{event.organizer.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {event.organizer.category}
                        </p>
                      </div>
                    </div>
                  )}

                  {event.team && (
                    <div className="flex items-center gap-3">
                      <Music2 className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{event.team.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {event.team.category}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {event.internal_notes && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="mb-2 font-medium">Notes internes</h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {event.internal_notes}
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Documents Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Documents</CardTitle>
                  {event.status === "First Contact" && (
                    <Button
                      onClick={() => setSendDocumentsOpen(true)}
                      disabled={!docRequirements.canSend}
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Envoyer les documents
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Devis */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        <h4 className="font-medium">Devis</h4>
                      </div>
                      {event.quote ? (
                        <Badge variant="default" className="bg-green-600">
                          <Check className="h-3 w-3 mr-1" />
                          Créé
                        </Badge>
                      ) : (
                        <Badge variant="secondary">À créer</Badge>
                      )}
                    </div>

                    {event.quote ? (
                      <div className="space-y-2">
                        <div className="grid gap-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Total</span>
                            <span className="font-bold">
                              {event.quote.total_amount}€
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          onClick={() => setQuoteOpen(true)}
                        >
                          Modifier le devis
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setQuoteOpen(true)}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Créer un devis
                      </Button>
                    )}
                  </div>

                  {/* Fiche Technique */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ClipboardList className="h-5 w-5" />
                        <h4 className="font-medium">Fiche technique</h4>
                      </div>
                      {event.technical_sheet ? (
                        <Badge variant="default" className="bg-green-600">
                          <Check className="h-3 w-3 mr-1" />
                          Créée
                        </Badge>
                      ) : (
                        <Badge variant="secondary">À créer</Badge>
                      )}
                    </div>

                    {event.technical_sheet ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setTechnicalSheetOpen(true)}
                      >
                        Modifier la fiche
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setTechnicalSheetOpen(true)}
                      >
                        <ClipboardList className="mr-2 h-4 w-4" />
                        Créer une fiche technique
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Validation Section - Only show when Documents Sent */}
            {event.status === "Documents Sent" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Lock className="h-5 w-5" />
                      Validation
                    </CardTitle>
                    {validationRequirements.canValidate && (
                      <Button onClick={handleValidate}>
                        <Check className="mr-2 h-4 w-4" />
                        Valider l'événement
                      </Button>
                    )}
                  </div>
                  <CardDescription>
                    L'événement sera confirmé une fois le devis signé ET
                    l'acompte reçu
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-2">
                        {validationRequirements.hasSignature ? (
                          <Check className="h-5 w-5 text-green-600" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
                        )}
                        <span>Signature du devis</span>
                      </div>
                      {validationRequirements.hasSignature ? (
                        <Badge variant="default" className="bg-green-600">
                          Reçue
                        </Badge>
                      ) : (
                        <Badge variant="secondary">En attente</Badge>
                      )}
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-2">
                        {validationRequirements.hasDepositPaid ? (
                          <Check className="h-5 w-5 text-green-600" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
                        )}
                        <span>Acompte (30%)</span>
                      </div>
                      {validationRequirements.hasDepositPaid ? (
                        <Badge variant="default" className="bg-green-600">
                          Reçu
                        </Badge>
                      ) : (
                        <Badge variant="secondary">En attente</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payments Section */}
            <PaymentTracking
              quoteId={event.quote?.id}
              totalAmount={event.quote?.total_amount ?? 0}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Prochaines étapes - Guide utilisateur */}
            <Card className="border-primary/50 bg-primary/5">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ChevronRight className="h-5 w-5 text-primary" />
                  Prochaines étapes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {event.status === "Project" && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground mb-3">
                      Nouvel événement. Voici ce qu'il faut faire :
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 text-sm">
                        <Phone className="h-4 w-4 mt-0.5 text-primary" />
                        <div>
                          <p className="font-medium">
                            Contacter l'organisateur
                          </p>
                          <p className="text-muted-foreground text-xs">
                            Appeler pour comprendre les besoins
                          </p>
                        </div>
                      </div>
                      <Button
                        className="w-full mt-2"
                        onClick={() =>
                          updateEvent.mutateAsync({
                            id: event.id,
                            updates: { status: "First Contact" },
                          })
                        }
                      >
                        Marquer comme "Premier contact"
                      </Button>
                    </div>
                  </div>
                )}

                {event.status === "First Contact" && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground mb-3">
                      Contact établi. Préparez les documents :
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        {event.quote ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <Circle className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span
                          className={
                            event.quote
                              ? "line-through text-muted-foreground"
                              : ""
                          }
                        >
                          Créer le devis
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        {event.technical_sheet ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <Circle className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span
                          className={
                            event.technical_sheet
                              ? "line-through text-muted-foreground"
                              : ""
                          }
                        >
                          Créer la fiche technique
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        {!event.team_id ? (
                          <Circle className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        )}
                        <span
                          className={
                            event.team_id
                              ? "line-through text-muted-foreground"
                              : ""
                          }
                        >
                          Assigner une équipe (interne)
                        </span>
                      </div>
                    </div>
                    {docRequirements.canSend && (
                      <Button
                        className="w-full mt-3"
                        onClick={() => setSendDocumentsOpen(true)}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Envoyer les documents
                      </Button>
                    )}
                  </div>
                )}

                {event.status === "Documents Sent" && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground mb-3">
                      Documents envoyés. En attente de validation :
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        {validationRequirements.hasSignature ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <Circle className="h-4 w-4 text-amber-500" />
                        )}
                        <span>
                          {validationRequirements.hasSignature
                            ? "Devis signé"
                            : "Attendre signature du devis"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        {validationRequirements.hasDepositPaid ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <Circle className="h-4 w-4 text-amber-500" />
                        )}
                        <span>
                          {validationRequirements.hasDepositPaid
                            ? "Acompte reçu"
                            : "Attendre paiement acompte"}
                        </span>
                      </div>
                    </div>
                    {validationRequirements.canValidate && (
                      <Button className="w-full mt-3" onClick={handleValidate}>
                        <Check className="h-4 w-4 mr-2" />
                        Valider l'événement
                      </Button>
                    )}
                  </div>
                )}

                {event.status === "Validated" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-600 mb-2">
                      <Lock className="h-4 w-4" />
                      <span className="font-medium">Événement confirmé</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Préparez l'organisation finale :
                    </p>
                    <ul className="text-sm space-y-1 mt-2">
                      <li>• Confirmer les détails logistiques</li>
                      <li>• Briefer l'équipe</li>
                      <li>• Rappels J-7, J-3, J-1</li>
                    </ul>
                    <Button
                      className="w-full mt-3"
                      variant="outline"
                      onClick={() =>
                        updateEvent.mutateAsync({
                          id: event.id,
                          updates: { status: "Final Organization" },
                        })
                      }
                    >
                      Passer en "Organisation finale"
                    </Button>
                  </div>
                )}

                {event.status === "Final Organization" && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Dernière ligne droite :
                    </p>
                    <ul className="text-sm space-y-1 mt-2">
                      <li>• Vérifier tous les détails</li>
                      <li>• L'événement a lieu</li>
                      <li>• Collecter le solde</li>
                    </ul>
                    <Button
                      className="w-full mt-3"
                      onClick={() =>
                        updateEvent.mutateAsync({
                          id: event.id,
                          updates: { status: "Completed" },
                        })
                      }
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Marquer comme terminé
                    </Button>
                  </div>
                )}

                {event.status === "Completed" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">Événement terminé</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      N'oubliez pas la relecture interne et les remerciements.
                    </p>
                  </div>
                )}

                {event.status === "Abandoned" && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Cet événement a été abandonné.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Statut</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <EventStatusBadge status={event.status} />
                  {event.status === "Validated" && (
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <Lock className="h-4 w-4" />
                      Événement confirmé et verrouillé
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Informations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {event.expected_attendees && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Jauge</span>
                    <span>{event.expected_attendees} personnes</span>
                  </div>
                )}
                {event.budget_estimate && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Budget estimé</span>
                    <span>{event.budget_estimate}€</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Créé le</span>
                  <span>
                    {format(new Date(event.created_at), "d MMM yyyy", {
                      locale: fr,
                    })}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <EventForm
        open={editOpen}
        onClose={() => setEditOpen(false)}
        event={event}
      />

      <QuoteForm
        open={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        eventId={event.id}
      />

      <TechnicalSheetForm
        open={technicalSheetOpen}
        onClose={() => setTechnicalSheetOpen(false)}
        eventId={event.id}
      />

      <SendDocumentsDialog
        open={sendDocumentsOpen}
        onClose={() => setSendDocumentsOpen(false)}
        event={event}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l&apos;événement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L&apos;événement et toutes ses
              données associées seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
